const REQUIRED_BUNDLE_FIELDS = [
  "preferences",
  "preferencesUpdatedAt",
  "watchlist",
  "watchlistUpdatedAt",
  "theme",
  "themeUpdatedAt",
  "streak",
  "streakUpdatedAt",
  "audioPreferences",
  "todos",
  "deletedTodos",
  "savedArticles",
  "deletedSavedArticles",
  "serverTime",
];

function hasOwn(value, key) {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isNonEmptyString(value) {
  return typeof value === "string" && Boolean(value.trim());
}

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function isTimestamp(value) {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    Boolean(value) &&
    Number.isFinite(Date.parse(value))
  );
}

function isNullableTimestamp(value) {
  return value === null || isTimestamp(value);
}

function isNullableRecord(value) {
  return value === null || isRecord(value);
}

function isNullableString(value) {
  return value === null || typeof value === "string";
}

function isNullableFiniteNumber(value) {
  return value === null || isFiniteNumber(value);
}

function isTodoRow(value) {
  return (
    isRecord(value) &&
    isNonEmptyString(value.clientId) &&
    isNonEmptyString(value.text) &&
    typeof value.notes === "string" &&
    isFiniteNumber(value.priority) &&
    isNullableFiniteNumber(value.dueAtEpochMs) &&
    isNullableFiniteNumber(value.completedAtEpochMs) &&
    typeof value.tag === "string" &&
    isFiniteNumber(value.sortOrder) &&
    isTimestamp(value.updatedAt)
  );
}

function isSavedArticleRow(value) {
  return (
    isRecord(value) &&
    isNonEmptyString(value.articleId) &&
    isNonEmptyString(value.title) &&
    isNullableString(value.link) &&
    isNullableString(value.source) &&
    isNullableString(value.description) &&
    isNullableString(value.contentLicense) &&
    isNullableString(value.usageRights) &&
    isNullableString(value.categoryLabel) &&
    isNullableFiniteNumber(value.savedAtEpochMs) &&
    isTimestamp(value.updatedAt)
  );
}

function isTombstone(value, idKey) {
  return (
    isRecord(value) &&
    isNonEmptyString(value[idKey]) &&
    (value.mutationId === null || isNonEmptyString(value.mutationId)) &&
    isTimestamp(value.deletedAt)
  );
}

function hasUniqueIds(rows, idKey) {
  const ids = rows.map((row) => row[idKey]);
  return new Set(ids).size === ids.length;
}

function activeAndDeletedAreDisjoint(active, deleted, idKey) {
  const activeIds = new Set(active.map((row) => row[idKey]));
  return deleted.every((row) => !activeIds.has(row[idKey]));
}

/**
 * A pull cursor is safe only when the complete snapshot/change-set contract
 * arrived intact. Return the original object after validating every domain so
 * callers can apply it atomically without normalization silently dropping rows.
 */
export function readCompleteSyncPullBundle(value) {
  if (
    !isRecord(value) ||
    !REQUIRED_BUNDLE_FIELDS.every((field) => hasOwn(value, field)) ||
    !isNullableRecord(value.preferences) ||
    !isNullableTimestamp(value.preferencesUpdatedAt) ||
    !(value.watchlist === null ||
      (Array.isArray(value.watchlist) &&
        value.watchlist.every(isNonEmptyString))) ||
    !isNullableTimestamp(value.watchlistUpdatedAt) ||
    !isNullableRecord(value.theme) ||
    !isNullableTimestamp(value.themeUpdatedAt) ||
    !isNullableRecord(value.streak) ||
    !isNullableTimestamp(value.streakUpdatedAt) ||
    !isNullableRecord(value.audioPreferences) ||
    !Array.isArray(value.todos) ||
    !value.todos.every(isTodoRow) ||
    !Array.isArray(value.deletedTodos) ||
    !value.deletedTodos.every((row) => isTombstone(row, "clientId")) ||
    !Array.isArray(value.savedArticles) ||
    !value.savedArticles.every(isSavedArticleRow) ||
    !Array.isArray(value.deletedSavedArticles) ||
    !value.deletedSavedArticles.every((row) =>
      isTombstone(row, "articleId")) ||
    !isTimestamp(value.serverTime) ||
    !hasUniqueIds(value.todos, "clientId") ||
    !hasUniqueIds(value.deletedTodos, "clientId") ||
    !hasUniqueIds(value.savedArticles, "articleId") ||
    !hasUniqueIds(value.deletedSavedArticles, "articleId") ||
    !activeAndDeletedAreDisjoint(
      value.todos,
      value.deletedTodos,
      "clientId",
    ) ||
    !activeAndDeletedAreDisjoint(
      value.savedArticles,
      value.deletedSavedArticles,
      "articleId",
    )
  ) {
    const error = new Error("sync_pull_invalid_response");
    error.code = "sync_pull_invalid_response";
    throw error;
  }
  return value;
}
